import { useState } from "react";
import { MapPin, Mail, Navigation, ArrowRight, Copy, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { WhatsAppIcon } from "./Icons";
import {
  WHATSAPP_LINK,
  WHATSAPP_DISPLAY,
  EMAIL,
  SALES_EMAIL,
  LOCATION,
} from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

const CopyButton = ({ value, labelCopy, labelCopied }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button
      type="button"
      data-testid="copy-whatsapp-button"
      onClick={handleCopy}
      aria-label={copied ? labelCopied : labelCopy}
      title={copied ? labelCopied : labelCopy}
      className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-300 ${
        copied
          ? "border-forest bg-forest text-cream"
          : "border-gold/40 text-clay hover:border-gold hover:bg-gold hover:text-white"
      }`}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
};

const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  return (
  <section id="contact" data-testid="contact-section" className="bg-cream pb-24 pt-24 md:pb-32">
    {/* CTA band */}
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <Reveal>
        <div className="grid grid-cols-1 items-center gap-8 border border-gold/30 bg-[#F3EEE6] p-10 md:grid-cols-2 md:p-14">
          <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-[1.05]">
            {c.ctaTitle}
          </h2>
          <div className="flex flex-col items-start gap-6 md:items-end">
            <div className="w-full md:w-[400px]">
  <div className="flex flex-col gap-5">
    {/* WhatsApp */}
    <div className="grid grid-cols-[44px_minmax(0,1fr)_36px] items-center gap-3">
      <a
        data-testid="contact-whatsapp"
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="contents"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest text-cream">
          <WhatsAppIcon className="h-5 w-5" />
        </span>

        <span className="min-w-0">
          <span className="overline block text-clay/70 text-[0.6rem]">
            {c.chatWhatsapp}
          </span>
          <span
            data-testid="contact-whatsapp-number"
            className="font-medium"
          >
            {WHATSAPP_DISPLAY}
          </span>
        </span>
      </a>

      <CopyButton
        value={WHATSAPP_DISPLAY}
        labelCopy={t.common.copy}
        labelCopied={t.common.copied}
      />
    </div>

    {/* Email */}
    <div className="grid grid-cols-[44px_minmax(0,1fr)_36px] items-start gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest text-cream">
        <Mail className="h-5 w-5" />
      </span>

      <div className="min-w-0">
        <span className="overline block text-clay/70 text-[0.6rem]">
          {c.emailUs}
        </span>

        <a
          data-testid="contact-email"
          href={`mailto:${EMAIL}`}
          className="block font-medium break-all text-ink"
        >
          {EMAIL}
        </a>

        <a
          href={`mailto:${SALES_EMAIL}`}
          className="mt-1 block font-medium break-all text-ink"
        >
          {SALES_EMAIL}
        </a>
      </div>
    </div>
  </div>
</div>
            <a
              data-testid="contact-quote-button"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-golddark text-white overline px-8 py-4 transition-colors duration-300"
            >
              {t.common.getQuote}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </Reveal>
    </div>

    {/* Map + address */}
    <div className="mx-auto mt-14 max-w-[1400px] px-5 md:px-10">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <p className="overline text-gold">{c.visitUs}</p>
          <h3 className="mt-3 font-serif text-3xl text-ink">{LOCATION.label}</h3>
          <div className="mt-6 flex items-start gap-3 text-clay">
            <MapPin className="mt-1 h-5 w-5 shrink-0 text-gold" />
            <p className="leading-relaxed">{LOCATION.address}</p>
          </div>
          <a
            data-testid="get-directions-button"
            href={LOCATION.directions}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 border border-ink/80 px-6 py-4 overline text-ink transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-white"
          >
            <Navigation className="h-4 w-4" />
            {c.getDirections}
          </a>
        </Reveal>

        <Reveal className="lg:col-span-3">
          <div className="spotlight h-[360px] w-full overflow-hidden border border-gold/25 md:h-[420px]">
            <iframe
              data-testid="location-map"
              title="Tropical Vanilla Co. location"
              src={LOCATION.embed}
              className="h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

export default Contact;
